import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = import.meta.env.VITE_API_GATEWAY_URL || '';

export type LeadSource = 'meta-ads' | 'google-ads' | 'youtube' | 'website' | 'referral' | 'direct';
export type LeadStatus = 'enquired' | 'contacted' | 'counselling' | 'registered' | 'enrolled' | 'dropped';

export interface FollowUp {
  id?: string;
  date: string;
  note: string;
  type?: 'whatsapp_inbound' | 'whatsapp_outbound' | 'call' | 'note' | 'meeting' | 'status_change';
  author?: string;
  channel?: 'whatsapp' | 'crm' | 'phone' | 'email';
}

export interface Lead {
  leadId: string;
  name: string;
  email: string;
  phone: string;
  source: LeadSource | string;
  status: LeadStatus;
  assignedTo: string;
  courseInterest: string;
  fee: number;
  paid1: number;
  paid2: number;
  due: number;
  notes: string;
  followUps: FollowUp[];
  lastFollowUp: string | null;
  nextFollowUp: string | null;
  studentId?: string;
  convertedAt?: string;
  metadata?: {
    rawMessage?: string;
    referralData?: any;
    capturedVia?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface LeadAnalytics {
  totalLeads: number;
  totalEnrolled: number;
  overallConversionRate: number;
  totalRevenue: number;
  sourceBreakdown: Record<string, {
    source: string;
    total: number;
    enquired: number;
    contacted: number;
    counselling: number;
    registered: number;
    enrolled: number;
    dropped: number;
    totalRevenue: number;
    totalDue: number;
    conversionRate: number;
  }>;
}

class LeadsService {
  private async getAuthToken(): Promise<string> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || '';
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return '';
    }
  }

  async createLead(data: {
    name: string;
    email?: string;
    phone?: string;
    source: string;
    assignedTo?: string;
    courseInterest?: string;
    notes?: string;
    fee?: number;
    paid1?: number;
    paid2?: number;
    due?: number;
  }): Promise<Lead> {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/leads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'create',
        ...data,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || 'Failed to create lead');
    }

    const result = await response.json();
    return result.lead;
  }

  async updateLead(leadId: string, updates: Partial<Lead>): Promise<Lead> {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/leads`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'update',
        leadId,
        ...updates,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || 'Failed to update lead');
    }

    const result = await response.json();
    return result.lead;
  }

  async deleteLead(leadId: string): Promise<void> {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/leads?action=delete&leadId=${leadId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || 'Failed to delete lead');
    }
  }

  async getLead(leadId: string): Promise<Lead> {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/leads?action=get&leadId=${leadId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get lead');
    }

    const result = await response.json();
    return result.lead;
  }

  async listLeads(filters?: { status?: string; source?: string; assignedTo?: string }): Promise<Lead[]> {
    const token = await this.getAuthToken();
    const params = new URLSearchParams({ action: 'list' });
    
    if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters?.source && filters.source !== 'all') params.append('source', filters.source);
    if (filters?.assignedTo) params.append('assignedTo', filters.assignedTo);

    const response = await fetch(`${API_BASE_URL}/leads?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to list leads');
    }

    const result = await response.json();
    return result.leads || [];
  }

  async searchLeads(query: string): Promise<Lead[]> {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/leads?action=search&q=${encodeURIComponent(query)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to search leads');
    }

    const result = await response.json();
    return result.leads || [];
  }

  async updateFeeStatus(leadId: string, fee: number, paid1: number, paid2: number | string): Promise<Lead> {
    const token = await this.getAuthToken();
    const paid2Num = typeof paid2 === 'string' ? parseFloat(paid2) || 0 : paid2 || 0;
    const paid1Num = typeof paid1 === 'string' ? parseFloat(paid1) || 0 : paid1 || 0;
    const feeNum = typeof fee === 'string' ? parseFloat(fee) || 0 : fee || 0;
    const due = Math.max(0, feeNum - paid1Num - paid2Num);
    
    const response = await fetch(`${API_BASE_URL}/leads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'update-fee',
        leadId,
        fee: feeNum,
        paid1: paid1Num,
        paid2: paid2Num,
        due,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to update fee status');
    }

    const result = await response.json();
    return result.lead;
  }

  async addFollowUp(
    leadId: string, 
    note: string, 
    nextFollowUp?: string, 
    type: 'whatsapp_inbound' | 'whatsapp_outbound' | 'call' | 'note' | 'meeting' = 'note',
    author: string = 'Admissions Staff',
    newStatus?: LeadStatus
  ): Promise<Lead> {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/leads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'add-follow-up',
        leadId,
        note,
        nextFollowUp,
        type,
        author,
        newStatus,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to log follow-up');
    }

    const result = await response.json();
    return result.lead;
  }

  async convertToStudent(leadId: string, notes?: string): Promise<{ lead: Lead; studentId: string }> {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/leads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'convert-to-student',
        leadId,
        notes,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || 'Failed to convert lead to student');
    }

    const result = await response.json();
    return { lead: result.lead, studentId: result.studentId };
  }

  async simulateWhatsAppInbound(data: {
    phone: string;
    name: string;
    message: string;
    source: string;
    courseInterest?: string;
  }): Promise<{ isNew: boolean; lead: Lead }> {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/leads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'whatsapp-inbound',
        ...data,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || 'Failed to ingest WhatsApp webhook');
    }

    return await response.json();
  }

  async autoAssignCourseFees(): Promise<void> {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/leads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'auto-assign-course-fees',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to auto-assign course fees');
    }
  }

  async getAnalytics(): Promise<LeadAnalytics> {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/leads?action=get-analytics`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch lead analytics');
    }

    return await response.json();
  }
}

export const leadsService = new LeadsService();
